import {
   Container,
   Paper,
   Stack,
   Title,
   Text,
   Grid,
   Group,
   ThemeIcon,
   Table,
   Loader,
   Button,
   Pagination,
   Select,
   Badge,
} from "@mantine/core";
import { IconWallet, IconCalendar, IconDownload, IconRefresh } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface Transaction {
   id: string;
   userID: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
   };
   type: string;
   status: string;
   method: string;
   amount: number;
   description: string;
   createdAt: number;
}

interface TransactionSummary {
   totalCount: number;
   totalAmount: number;
   byType: Record<string, { count: number; total: number }>;
   byStatus: Record<string, { count: number; total: number }>;
   byMethod: Record<string, { count: number; total: number }>;
}

interface TransactionResponse {
   data: Transaction[];
   count: number;
   totalRecords: number;
   page: number;
   limit: number;
   summary: TransactionSummary;
}

export default function TransactionReport() {
   const [startDate, setStartDate] = useState<Date | null>(null);
   const [endDate, setEndDate] = useState<Date | null>(null);
   const [type, setType] = useState<string | null>(null);
   const [status, setStatus] = useState<string | null>(null);
   const [page, setPage] = useState(1);
   const limit = 20;
   const [filters, setFilters] = useState<{
      startDate?: number;
      endDate?: number;
      type?: string;
      status?: string;
   }>({});

   const {
      data: reportData,
      isLoading,
      reloadData,
   } = usePerformRequest<TransactionResponse>({
      method: Endpoints.GetTransactionReport(
         page,
         limit,
         filters.startDate,
         filters.endDate,
         filters.type,
         filters.status
      ).method,
      route: Endpoints.GetTransactionReport(
         page,
         limit,
         filters.startDate,
         filters.endDate,
         filters.type,
         filters.status
      ).route,
   });

   useEffect(() => {
      const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
         if (!date) return undefined;
         if (date instanceof Date) return date.getTime();
         if (typeof date === "string") {
            const parsed = new Date(date);
            return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
         }
         return undefined;
      };
      const start = getTimestamp(startDate);
      const end = getTimestamp(endDate);
      setFilters({
         startDate: start,
         endDate: end,
         type: type || undefined,
         status: status || undefined,
      });
   }, [startDate, endDate, type, status]);

   useEffect(() => {
      reloadData();
   }, [filters, page]);

   const formatCurrency = (amount: number) => {
      return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
   };

   const formatDate = (timestamp: number) => {
      return new Date(timestamp).toLocaleString("en-US", {
         year: "numeric",
         month: "short",
         day: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   const getStatusColor = (status: string) => {
      switch (status) {
         case "completed":
            return "green";
         case "pending":
            return "yellow";
         case "failed":
            return "red";
         case "cancelled":
            return "gray";
         default:
            return "blue";
      }
   };

   const getTypeColor = (type: string) => {
      switch (type) {
         case "loan_disbursement":
            return "blue";
         case "loan_repayment":
            return "green";
         case "wallet_funding":
            return "cyan";
         case "wallet_withdrawal":
            return "orange";
         case "fee":
            return "purple";
         default:
            return "gray";
      }
   };

   const transactions = reportData?.data || [];
   const summary = reportData?.summary;
   const totalPages = reportData ? Math.ceil(reportData.totalRecords / limit) : 1;

   const statCards = summary
      ? [
           {
              label: "Total Transactions",
              value: summary.totalCount.toLocaleString(),
              color: "#699269",
           },
           {
              label: "Total Amount",
              value: formatCurrency(summary.totalAmount),
              color: "#558B2F",
           },
        ]
      : [];

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            <Group justify="space-between" align="flex-start">
               <Group gap="md">
                  <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
                     <IconWallet size={24} color="white" />
                  </ThemeIcon>
                  <div>
                     <Title order={2} fw={700}>
                        Transaction Report
                     </Title>
                     <Text size="sm" c="dimmed">
                        Complete transaction history.
                     </Text>
                  </div>
               </Group>
               <Group gap="sm">
                  <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
                     Refresh
                  </Button>
                  <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
                     Export
                  </Button>
               </Group>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
               <Grid gutter="md">
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                     <DatePickerInput
                        label="Start Date"
                        placeholder="Select start date"
                        value={startDate}
                        onChange={(value) => setStartDate(value as unknown as Date | null)}
                        leftSection={<IconCalendar size={16} />}
                        clearable
                     />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                     <DatePickerInput
                        label="End Date"
                        placeholder="Select end date"
                        value={endDate}
                        onChange={(value) => setEndDate(value as unknown as Date | null)}
                        leftSection={<IconCalendar size={16} />}
                        clearable
                        minDate={
                           startDate
                              ? startDate instanceof Date
                                 ? new Date(startDate.getTime() + 86400000)
                                 : new Date(new Date(startDate as string).getTime() + 86400000)
                              : undefined
                        }
                     />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                     <Select
                        label="Type"
                        placeholder="All types"
                        value={type}
                        onChange={setType}
                        data={[
                           { value: "loan_disbursement", label: "Loan Disbursement" },
                           { value: "loan_repayment", label: "Loan Repayment" },
                           { value: "wallet_funding", label: "Wallet Funding" },
                           { value: "wallet_withdrawal", label: "Wallet Withdrawal" },
                           { value: "fee", label: "Fee" },
                        ]}
                        clearable
                     />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                     <Select
                        label="Status"
                        placeholder="All statuses"
                        value={status}
                        onChange={setStatus}
                        data={[
                           { value: "completed", label: "Completed" },
                           { value: "pending", label: "Pending" },
                           { value: "failed", label: "Failed" },
                           { value: "cancelled", label: "Cancelled" },
                        ]}
                        clearable
                     />
                  </Grid.Col>
               </Grid>
            </Paper>

            {isLoading ? (
               <Paper shadow="sm" p="xl" radius="md" withBorder>
                  <Group justify="center">
                     <Loader size="lg" />
                  </Group>
               </Paper>
            ) : (
               <>
                  {summary && (
                     <Grid gutter="md">
                        {statCards.map((card, index) => (
                           <Grid.Col key={card.label} span={{ base: 12, sm: 6 }}>
                              <motion.div
                                 initial={{ opacity: 0, y: 20 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 transition={{ delay: index * 0.1 }}
                              >
                                 <Paper shadow="sm" p="lg" radius="md" withBorder>
                                    <Stack gap="xs">
                                       <Text size="sm" c="dimmed" fw={500}>
                                          {card.label}
                                       </Text>
                                       <Text size="xl" fw={700} style={{ color: card.color }}>
                                          {card.value}
                                       </Text>
                                    </Stack>
                                 </Paper>
                              </motion.div>
                           </Grid.Col>
                        ))}
                     </Grid>
                  )}

                  <Paper shadow="sm" p="lg" radius="md" withBorder>
                     <Title order={4} mb="md">
                        Transactions
                     </Title>
                     {transactions.length > 0 ? (
                        <>
                           <Table.ScrollContainer minWidth={800}>
                              <Table striped highlightOnHover>
                                 <Table.Thead>
                                    <Table.Tr>
                                       <Table.Th>User</Table.Th>
                                       <Table.Th>Type</Table.Th>
                                       <Table.Th>Status</Table.Th>
                                       <Table.Th>Method</Table.Th>
                                       <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                                       <Table.Th>Date</Table.Th>
                                    </Table.Tr>
                                 </Table.Thead>
                                 <Table.Tbody>
                                    {transactions.map((transaction) => (
                                       <Table.Tr key={transaction.id}>
                                          <Table.Td>
                                             <Text size="sm" fw={500}>
                                                {transaction.userID?.firstName} {transaction.userID?.lastName}
                                             </Text>
                                             <Text size="xs" c="dimmed">
                                                {transaction.userID?.email}
                                             </Text>
                                          </Table.Td>
                                          <Table.Td>
                                             <Badge color={getTypeColor(transaction.type)} size="sm">
                                                {transaction.type.replace("_", " ")}
                                             </Badge>
                                          </Table.Td>
                                          <Table.Td>
                                             <Badge color={getStatusColor(transaction.status)} size="sm">
                                                {transaction.status}
                                             </Badge>
                                          </Table.Td>
                                          <Table.Td>
                                             <Text size="sm" tt="capitalize">
                                                {transaction.method.replace("_", " ")}
                                             </Text>
                                          </Table.Td>
                                          <Table.Td style={{ textAlign: "right" }}>
                                             <Text fw={600}>{formatCurrency(transaction.amount)}</Text>
                                          </Table.Td>
                                          <Table.Td>
                                             <Text size="sm">{formatDate(transaction.createdAt)}</Text>
                                          </Table.Td>
                                       </Table.Tr>
                                    ))}
                                 </Table.Tbody>
                              </Table>
                           </Table.ScrollContainer>
                           {totalPages > 1 && (
                              <Group justify="center" mt="xl">
                                 <Pagination value={page} onChange={setPage} total={totalPages} />
                              </Group>
                           )}
                        </>
                     ) : (
                        <Text c="dimmed" ta="center" py="xl">
                           No transactions found
                        </Text>
                     )}
                  </Paper>
               </>
            )}
         </Stack>
      </Container>
   );
}
